import React from 'react'
import { Button, Text, LoadingSpinner, SortableTableHeader } from '../../components/ui'
import { AppointmentFormModal } from '../../components/centralAmparo/AppointmentFormModal'
import { AgendaCancelModal } from '../../components/centralAmparo/AgendaCancelModal'
import { AgendaAttentionModal } from '../../components/centralAmparo/AgendaAttentionModal'
import type { SupportCenterAppointmentCancelChannel } from '../../types/supportCenterAppointments'
import { TriagemCaseModal } from '../../components/centralAmparo/TriagemCaseModal'
import { CheckboxFilterDropdown } from '../../components/centralAmparo/CheckboxFilterDropdown'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { supportCenterSpecialtiesService } from '../../services/supportCenterSpecialties'
import { supportCenterTriageCatalogService } from '../../services/supportCenterTriageCatalog'
import { supportCenterTriageService } from '../../services/supportCenterTriage'
import { supportCenterCancelReasonsService } from '../../services/supportCenterCancelReasons'
import { supportCenterParametersService } from '../../services/supportCenterParameters'
import {
  isAppointmentPastForAttendance,
  triagePastMessage,
} from '../../services/supportCenterAttendance'
import type {
  SupportCenterProfileOption,
  SupportCenterSpecialization,
} from '../../types/supportCenterSpecialties'
import type { SupportCenterCancelReason } from '../../types/supportCenterCancelReasons'
import type {
  SupportCenterDemandType,
  SupportCenterReferralOrigin,
  SupportCenterTriageListItem,
  SupportCenterTriageStatus,
} from '../../types/supportCenterTriage'
import { TRIAGE_STATUS_LABELS } from '../../types/supportCenterTriage'
import { DEFAULT_SCHEDULING_LIMITS } from '../../types/supportCenterParameters'
import {
  DASHBOARD_INPUT_CLASS,
  formatCpf,
  formatDateTimeLines,
  formatPhone,
  getErrorMessage,
  patientKinshipLabel,
} from '../../components/centralAmparo/dashboardHelpers'
import { statusBadgeClass } from '../../components/centralAmparo/triagemHelpers'

const MODULE_NAME = 'central_amparo_triagem'

type TriagemSortKey =
  | 'scheduled_at'
  | 'holder_name'
  | 'patient_name'
  | 'specialization_name'
  | 'professional_name'
  | 'status'

function compareSortValues(a: string, b: string, order: 'asc' | 'desc'): number {
  const cmp = a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' })
  return order === 'asc' ? cmp : -cmp
}

function sortValueForRow(row: SupportCenterTriageListItem, key: TriagemSortKey): string {
  switch (key) {
    case 'scheduled_at':
      return row.appointment?.scheduled_at || ''
    case 'specialization_name':
      return (row.appointment?.specialization?.name || '').trim()
    case 'professional_name':
      return (row.appointment?.professional?.name || '').trim()
    case 'status':
      return TRIAGE_STATUS_LABELS[row.status] || row.status
    case 'holder_name':
      return (row.appointment?.holder_people?.name || '').trim()
    case 'patient_name':
      return (row.appointment?.patient_people?.name || '').trim()
  }
}

type TriageListFilters = {
  statuses: SupportCenterTriageStatus[]
  specializationIds: string[]
  professionalUserIds: string[]
  triagedByIds: string[]
  dateFrom: string
  dateTo: string
  cpf: string
  phone: string
}

function defaultTriageFilters(): TriageListFilters {
  return {
    statuses: ['pendente'],
    specializationIds: [],
    professionalUserIds: [],
    triagedByIds: [],
    dateFrom: '',
    dateTo: '',
    cpf: '',
    phone: '',
  }
}

type TriagemCasosTabProps = {
  canUpdate: boolean
  catalogRevision?: number
  onCreateActionChange?: (action: {
    visible: boolean
    disabled: boolean
    onClick: () => void
  } | null) => void
}

const TriagemCasosTab: React.FC<TriagemCasosTabProps> = ({
  canUpdate,
  catalogRevision = 0,
  onCreateActionChange,
}) => {
  const { user, profile } = useAuthContext()
  const actorId = user?.id || profile?.id || null
  const { logCreate, logUpdate } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(
    MODULE_NAME,
    'view_values'
  )

  const [searching, setSearching] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)

  const [specializations, setSpecializations] = React.useState<SupportCenterSpecialization[]>([])
  const [formSpecializations, setFormSpecializations] = React.useState<SupportCenterSpecialization[]>(
    []
  )
  const [userSpecializationIds, setUserSpecializationIds] = React.useState<string[]>([])
  /** Sem ver valores: true após carregar os vínculos de especialidade do usuário. */
  const [ownSpecIdsLoaded, setOwnSpecIdsLoaded] = React.useState(false)
  /** Profissionais com especialidade (filtro da agenda / casos). */
  const [profiles, setProfiles] = React.useState<SupportCenterProfileOption[]>([])
  /** Responsáveis pela triagem (função Triagem ou Atendente/Triagem). */
  const [triageStaffProfiles, setTriageStaffProfiles] = React.useState<
    SupportCenterProfileOption[]
  >([])
  const [triageActorProfiles, setTriageActorProfiles] = React.useState<SupportCenterProfileOption[]>(
    []
  )
  const [referralOrigins, setReferralOrigins] = React.useState<SupportCenterReferralOrigin[]>([])
  const [demandTypes, setDemandTypes] = React.useState<SupportCenterDemandType[]>([])
  const [cases, setCases] = React.useState<SupportCenterTriageListItem[]>([])

  const [filterStatuses, setFilterStatuses] = React.useState<SupportCenterTriageStatus[]>([
    'pendente',
  ])
  const [filterSpecializationIds, setFilterSpecializationIds] = React.useState<string[]>([])
  const [filterProfessionalUserIds, setFilterProfessionalUserIds] = React.useState<string[]>([])
  const [filterTriagedByIds, setFilterTriagedByIds] = React.useState<string[]>([])
  const [filterDateFrom, setFilterDateFrom] = React.useState('')
  const [filterDateTo, setFilterDateTo] = React.useState('')
  const [filterCpf, setFilterCpf] = React.useState('')
  const [filterPhone, setFilterPhone] = React.useState('')
  const [appliedFilters, setAppliedFilters] = React.useState<TriageListFilters>(defaultTriageFilters)
  const [searchToken, setSearchToken] = React.useState(0)
  const [sortBy, setSortBy] = React.useState<TriagemSortKey>('scheduled_at')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc')

  const [selectedCase, setSelectedCase] = React.useState<SupportCenterTriageListItem | null>(null)
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [cancelTarget, setCancelTarget] = React.useState<SupportCenterTriageListItem | null>(null)
  const [cancelReasonId, setCancelReasonId] = React.useState('')
  const [cancellationChannel, setCancellationChannel] = React.useState<
    '' | SupportCenterAppointmentCancelChannel
  >('')
  const [cancelReasons, setCancelReasons] = React.useState<SupportCenterCancelReason[]>([])
  const [loadingCancelReasons, setLoadingCancelReasons] = React.useState(false)
  const [cancelling, setCancelling] = React.useState(false)
  const [cancelError, setCancelError] = React.useState<string | null>(null)
  const [appointmentFormOpen, setAppointmentFormOpen] = React.useState(false)
  const appointmentFormOpenRef = React.useRef(false)
  appointmentFormOpenRef.current = appointmentFormOpen
  const [pastToleranceMinutes, setPastToleranceMinutes] = React.useState(
    DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes
  )

  React.useEffect(() => {
    void supportCenterParametersService.getSchedulingLimits().then((limits) => {
      setPastToleranceMinutes(limits.pastToleranceMinutes)
    })
  }, [])

  const statusIsDefault =
    appliedFilters.statuses.length === 0 ||
    (appliedFilters.statuses.length === 1 && appliedFilters.statuses[0] === 'pendente')
  const hasActiveFilters =
    !statusIsDefault ||
    appliedFilters.specializationIds.length > 0 ||
    appliedFilters.professionalUserIds.length > 0 ||
    appliedFilters.triagedByIds.length > 0 ||
    Boolean(appliedFilters.dateFrom) ||
    Boolean(appliedFilters.dateTo) ||
    Boolean(appliedFilters.cpf.trim()) ||
    Boolean(appliedFilters.phone.trim())

  const handleBuscar = () => {
    setAppliedFilters({
      statuses: filterStatuses,
      specializationIds: filterSpecializationIds,
      professionalUserIds: filterProfessionalUserIds,
      triagedByIds: filterTriagedByIds,
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
      cpf: filterCpf,
      phone: filterPhone,
    })
    setSearchToken((token) => token + 1)
  }

  const handleClearFilters = () => {
    const cleared = defaultTriageFilters()
    setFilterStatuses(cleared.statuses)
    setFilterSpecializationIds(cleared.specializationIds)
    setFilterProfessionalUserIds(cleared.professionalUserIds)
    setFilterTriagedByIds(cleared.triagedByIds)
    setFilterDateFrom(cleared.dateFrom)
    setFilterDateTo(cleared.dateTo)
    setFilterCpf(cleared.cpf)
    setFilterPhone(cleared.phone)
    setAppliedFilters(cleared)
    setSearchToken((token) => token + 1)
  }

  const handleFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleBuscar()
  }

  React.useEffect(() => {
    if (loadingViewValues) return
    let cancelled = false

    const loadCatalog = async () => {
      try {
        if (!canViewValues && !actorId) {
          if (cancelled) return
          setSpecializations([])
          setFormSpecializations([])
          setUserSpecializationIds([])
          setOwnSpecIdsLoaded(false)
          setProfiles([])
          setTriageStaffProfiles([])
          setTriageActorProfiles([])
          setReferralOrigins([])
          setDemandTypes([])
          return
        }

        const [specs, profileRows, triageStaffRows, triageActorRows, origins, demands, ownSpecIds] =
          await Promise.all([
            supportCenterSpecialtiesService.listSpecializations(),
            supportCenterSpecialtiesService.listAssignedProfiles(),
            supportCenterSpecialtiesService.listTriageStaffProfiles(),
            supportCenterTriageService.listTriagedByProfiles(),
            supportCenterTriageCatalogService.listReferralOrigins(),
            supportCenterTriageCatalogService.listDemandTypes(),
            canViewValues || !actorId
              ? Promise.resolve([] as string[])
              : supportCenterSpecialtiesService.listSpecializationIdsForUser(actorId),
          ])

        if (cancelled) return
        setSpecializations(specs)
        setFormSpecializations(specs)
        setUserSpecializationIds(canViewValues ? [] : ownSpecIds)
        setOwnSpecIdsLoaded(true)
        setProfiles(profileRows)
        setTriageStaffProfiles(triageStaffRows)
        setTriageActorProfiles(triageActorRows)
        setReferralOrigins(origins)
        setDemandTypes(demands)
      } catch (error) {
        if (!cancelled) setPageError(getErrorMessage(error))
      }
    }

    void loadCatalog()
    return () => {
      cancelled = true
    }
  }, [actorId, canViewValues, loadingViewValues])

  const loadCases = React.useCallback(async () => {
    if (loadingViewValues) return
    // Com o modal aberto, um spinner de página desmonta o formulário e apaga o resumo.
    const silent = appointmentFormOpenRef.current
    if (!silent) setSearching(true)
    setPageError(null)
    try {
      if (!canViewValues && !actorId) {
        setCases([])
        setPageError('Não foi possível identificar o usuário logado para filtrar as triagens.')
        return
      }

      // Sem ver valores: só casos do próprio profissional (professional_user_id).
      const scopedProfessionalIds = canViewValues
        ? appliedFilters.professionalUserIds
        : actorId
          ? [actorId]
          : undefined

      const rows = await supportCenterTriageService.listCases({
        statuses: appliedFilters.statuses,
        specialization_filter_ids: appliedFilters.specializationIds,
        professional_user_ids: scopedProfessionalIds,
        triaged_by_ids: appliedFilters.triagedByIds,
        date_from: appliedFilters.dateFrom || undefined,
        date_to: appliedFilters.dateTo || undefined,
        cpf: appliedFilters.cpf || undefined,
        phone: appliedFilters.phone || undefined,
      })
      setCases(rows)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      if (!silent) setSearching(false)
    }
  }, [actorId, appliedFilters, canViewValues, loadingViewValues])

  React.useEffect(() => {
    if (loadingViewValues || canViewValues) return
    setFilterProfessionalUserIds([])
  }, [loadingViewValues, canViewValues])

  React.useEffect(() => {
    if (loadingViewValues) return
    void loadCases()
  }, [loadCases, loadingViewValues, searchToken])

  React.useEffect(() => {
    if (!catalogRevision) return
    const reloadCatalogOnly = async () => {
      try {
        const [origins, demands] = await Promise.all([
          supportCenterTriageCatalogService.listReferralOrigins(),
          supportCenterTriageCatalogService.listDemandTypes(),
        ])
        setReferralOrigins(origins)
        setDemandTypes(demands)
      } catch (error) {
        setPageError(getErrorMessage(error))
      }
    }
    void reloadCatalogOnly()
  }, [catalogRevision])

  /** Novo agendamento: Criar + escopo do profissional pronto (sem ver valores). */
  const specialistScopeReady = canViewValues || ownSpecIdsLoaded
  const canCreateAppointment = !loadingViewValues && !!canInsert && specialistScopeReady

  const openCreate = React.useCallback(() => {
    if (!specialistScopeReady) return
    setAppointmentFormOpen(true)
  }, [specialistScopeReady])

  const openTriageCase = React.useCallback(
    (item: SupportCenterTriageListItem) => {
      const scheduledAt = item.appointment?.scheduled_at
      if (
        item.status === 'pendente' &&
        scheduledAt &&
        isAppointmentPastForAttendance(scheduledAt, pastToleranceMinutes)
      ) {
        setActionError(triagePastMessage(pastToleranceMinutes))
        return
      }
      setActionError(null)
      setSelectedCase(item)
    },
    [pastToleranceMinutes]
  )

  React.useEffect(() => {
    if (!onCreateActionChange) return
    onCreateActionChange({
      visible: !loadingViewValues && !!canInsert,
      disabled: formSpecializations.length === 0 || !specialistScopeReady,
      onClick: openCreate,
    })
    return () => onCreateActionChange(null)
  }, [
    onCreateActionChange,
    loadingViewValues,
    canInsert,
    formSpecializations.length,
    specialistScopeReady,
    openCreate,
  ])

  const openCancelFromList = React.useCallback(
    (item: SupportCenterTriageListItem) => {
      if (!canDelete || item.status !== 'pendente') return
      setCancelTarget(item)
      setCancelReasonId('')
      setCancellationChannel('')
      setCancelError(null)
      setLoadingCancelReasons(true)
      void supportCenterCancelReasonsService
        .listByKind('triagem')
        .then((list) => {
          setCancelReasons(list)
          if (list.length === 0) {
            setCancelError(
              'Nenhum motivo cadastrado. Cadastre em Parâmetros > Motivos de cancelamento > Triagem.'
            )
          }
        })
        .catch((error) => {
          setCancelReasons([])
          setCancelError(getErrorMessage(error))
        })
        .finally(() => setLoadingCancelReasons(false))
    },
    [canDelete]
  )

  const closeCancelFromList = () => {
    if (cancelling) return
    setCancelTarget(null)
    setCancelError(null)
    setCancellationChannel('')
  }

  const handleCancelFromList = async () => {
    if (!cancelTarget || !actorId || !canDelete || cancelling) return
    const selectedReason = cancelReasons.find((r) => r.id === cancelReasonId)
    if (!selectedReason) {
      setCancelError(
        cancelReasons.length === 0
          ? 'Nenhum motivo cadastrado. Cadastre em Parâmetros > Motivos de cancelamento > Triagem.'
          : 'Selecione o motivo do cancelamento.'
      )
      return
    }
    if (
      cancellationChannel !== 'whatsapp' &&
      cancellationChannel !== 'telefone' &&
      cancellationChannel !== 'videochamada'
    ) {
      setCancelError('Selecione o canal do cancelamento.')
      return
    }

    const contextAppointmentId = cancelTarget.appointment?.id?.trim()
    if (!contextAppointmentId) {
      setCancelError('Não há agendamento de contexto para cancelar com esta triagem.')
      return
    }

    const reason = selectedReason.name.trim()
    setCancelling(true)
    setCancelError(null)
    try {
      await supportCenterTriageService.cancelTriage({
        id: cancelTarget.id,
        appointment_id: contextAppointmentId,
        cancel_reason_id: selectedReason.id,
        actor_id: actorId,
        cancellation_channel: cancellationChannel,
      })
      const labelName =
        cancelTarget.appointment?.patient_people?.name ||
        cancelTarget.appointment?.patient_cpf ||
        ''
      void logUpdate('support_center_triage', cancelTarget.id, `Cancelou triagem: ${labelName} — ${reason}`)
      setCancelTarget(null)
      setCancellationChannel('')
      await loadCases()
    } catch (error) {
      setCancelError(getErrorMessage(error))
    } finally {
      setCancelling(false)
    }
  }

  const toggleSort = (key: string) => {
    const nextKey = key as TriagemSortKey
    if (sortBy === nextKey) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(nextKey)
      setSortOrder('asc')
    }
  }

  const sortedCases = React.useMemo(() => {
    return [...cases].sort((a, b) =>
      compareSortValues(sortValueForRow(a, sortBy), sortValueForRow(b, sortBy), sortOrder)
    )
  }, [cases, sortBy, sortOrder])

  return (
    <>
      {loadingViewValues ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : (
      <>
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Data inicial
              </label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                onKeyDown={handleFilterKeyDown}
                className={DASHBOARD_INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Data final
              </label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                onKeyDown={handleFilterKeyDown}
                className={DASHBOARD_INPUT_CLASS}
              />
            </div>
            <CheckboxFilterDropdown
              label="Especialidade"
              emptyLabel="Todas"
              options={specializations.map((spec) => ({ value: spec.id, label: spec.name }))}
              selected={filterSpecializationIds}
              onChange={setFilterSpecializationIds}
            />
            {canViewValues ? (
              <CheckboxFilterDropdown
                label="Profissional"
                emptyLabel="Todos"
                options={profiles.map((profileOption) => ({
                  value: profileOption.id,
                  label: profileOption.name || profileOption.id,
                }))}
                selected={filterProfessionalUserIds}
                onChange={setFilterProfessionalUserIds}
              />
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Profissional
                </label>
                <input
                  type="text"
                  value={profile?.name?.trim() || 'Usuário não identificado'}
                  disabled
                  className={`${DASHBOARD_INPUT_CLASS} opacity-70 cursor-not-allowed`}
                  title="Sem permissão de ver valores: filtro travado na sua agenda"
                />
              </div>
            )}
            <CheckboxFilterDropdown
              label="Profissional da triagem"
              emptyLabel="Todos"
              options={triageActorProfiles.map((profileOption) => ({
                value: profileOption.id,
                label: profileOption.name || profileOption.id,
              }))}
              selected={filterTriagedByIds}
              onChange={setFilterTriagedByIds}
            />
            <CheckboxFilterDropdown
              label="Status da triagem"
              emptyLabel="Pendente"
              options={(
                Object.entries(TRIAGE_STATUS_LABELS) as Array<[SupportCenterTriageStatus, string]>
              ).map(([value, statusLabel]) => ({ value, label: statusLabel }))}
              selected={filterStatuses.length > 0 ? filterStatuses : ['pendente']}
              onChange={(next) => {
                const statuses = next as SupportCenterTriageStatus[]
                setFilterStatuses(statuses.length > 0 ? statuses : ['pendente'])
              }}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                CPF
              </label>
              <input
                type="text"
                value={filterCpf}
                onChange={(e) => setFilterCpf(e.target.value)}
                onKeyDown={handleFilterKeyDown}
                placeholder="Filtrar por CPF..."
                className={DASHBOARD_INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Telefone
              </label>
              <input
                type="text"
                value={filterPhone}
                onChange={(e) => setFilterPhone(formatPhone(e.target.value))}
                onKeyDown={handleFilterKeyDown}
                placeholder="(00) 00000-0000"
                inputMode="tel"
                maxLength={15}
                className={DASHBOARD_INPUT_CLASS}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                variant="solid"
                onClick={handleBuscar}
                disabled={searching}
                className="flex-1"
              >
                {searching ? 'Buscando...' : 'Buscar'}
              </Button>
              <Button
                variant="outline"
                onClick={handleClearFilters}
                disabled={searching}
                className="flex-1"
              >
                Limpar filtros
              </Button>
            </div>
          </div>
        </div>

        {pageError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <Text size="sm">{pageError}</Text>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <Text size="lg" className="text-gray-900 dark:text-white">
              Lista de triagens
            </Text>
            <Text size="sm" color="muted">
              {cases.length} {cases.length === 1 ? 'registro' : 'registros'}
            </Text>
          </div>

          {searching ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : cases.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Text size="sm" color="muted">
                {hasActiveFilters
                  ? 'Nenhum resultado para os filtros informados.'
                  : 'Nenhum atendimento de triagem pendente.'}
              </Text>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <SortableTableHeader
                      sortKey="scheduled_at"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3"
                      compact
                    >
                      Data
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="holder_name"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3"
                      compact
                    >
                      Segurado
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="patient_name"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3"
                      compact
                    >
                      Paciente
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="specialization_name"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3"
                      compact
                    >
                      Especialidade
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="professional_name"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3"
                      compact
                    >
                      Profissional
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="status"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3"
                      compact
                    >
                      Status
                    </SortableTableHeader>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedCases.map((item) => {
                    const scheduledParts = item.appointment?.scheduled_at
                      ? formatDateTimeLines(item.appointment.scheduled_at)
                      : null
                    const canCancelRow = !!canDelete && item.status === 'pendente'
                    const kinshipLabel = patientKinshipLabel({
                      holder_cpf: item.appointment?.holder_cpf,
                      patient_cpf: item.appointment?.patient_cpf,
                      relationship_to_holder: item.appointment?.relationship_to_holder,
                    })
                    return (
                      <tr
                        key={item.id}
                        onClick={() => openTriageCase(item)}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {scheduledParts ? (
                            <div className="leading-tight">
                              <div>{scheduledParts.date}</div>
                              {scheduledParts.time ? <div>{scheduledParts.time}</div> : null}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                          <div>{item.appointment?.holder_people?.name?.trim() || '?'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatCpf(item.appointment?.holder_cpf ?? '')}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                          <div>{item.appointment?.patient_people?.name?.trim() || '?'}</div>
                          {kinshipLabel ? (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {kinshipLabel}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                          {item.appointment?.specialization?.name ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                          {item.appointment?.professional?.name?.trim() || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(
                              item.status
                            )}`}
                          >
                            {TRIAGE_STATUS_LABELS[item.status]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                          <div className="inline-flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                openTriageCase(item)
                              }}
                            >
                              Abrir
                            </Button>
                            {canCancelRow ? (
                              <Button
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation()
                                  openCancelFromList(item)
                                }}
                              >
                                Cancelar
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedCase && (
        <TriagemCaseModal
          triageCase={selectedCase}
          canUpdate={canUpdate}
          profiles={triageStaffProfiles}
          referralOrigins={referralOrigins}
          demandTypes={demandTypes}
          pastToleranceMinutes={pastToleranceMinutes}
          onClose={() => setSelectedCase(null)}
          onSaved={loadCases}
        />
      )}

      {cancelTarget && (
        <AgendaCancelModal
          patientLabel={
            cancelTarget.appointment?.patient_people?.name ||
            cancelTarget.appointment?.patient_cpf ||
            ''
          }
          cancelReasonId={cancelReasonId}
          cancelReasons={cancelReasons}
          loadingCancelReasons={loadingCancelReasons}
          cancelling={cancelling}
          cancelError={cancelError}
          onReasonChange={setCancelReasonId}
          cancellationChannel={cancellationChannel}
          onCancellationChannelChange={setCancellationChannel}
          onClose={closeCancelFromList}
          onConfirm={() => void handleCancelFromList()}
          title="Cancelar triagem"
          description="Você está cancelando a triagem de {patient}. Somente o agendamento exibido nesta linha será cancelado."
          reasonLabel="Motivo do cancelamento"
          confirmLabel="Confirmar cancelamento"
          confirmingLabel="Cancelando..."
          pendingMessage="Confirmando o cancelamento, aguarde..."
        />
      )}
      </>
      )}

      <AppointmentFormModal
        open={appointmentFormOpen}
        mode="create"
        initial={null}
        specializations={formSpecializations}
        userSpecializationIds={canViewValues ? [] : userSpecializationIds}
        restrictToOwnProfessional={!canViewValues}
        currentUserId={actorId}
        canSubmit={canCreateAppointment}
        onClose={() => setAppointmentFormOpen(false)}
        onSaved={(event) => {
          void logCreate('support_center_appointment', event.id, 'Criou agendamento pela triagem da Central de Amparo')
          void loadCases()
        }}
      />

      <AgendaAttentionModal
        message={actionError}
        titleId="triagem-attention-modal-title"
        onClose={() => setActionError(null)}
      />
    </>
  )
}

export default TriagemCasosTab
